import { TFunction } from 'next-i18next'
import cleanDeep from 'clean-deep'

import { Preference } from '~/types/tutoring-api'
import { PreferredTime, Timeslot } from '~/types/booking'
import { UserSkill, UserRole } from '~/types/user-info-types'
import { countryData } from '~/shared/constants/locale.const'
import { AppLanguage } from '~/types/locale'
import { SourceType, TrackType } from '~/components/VideoPlayer/VideoPlayer.types'

export function range(length: number): number[] {
  return [...Array(length).keys()]
}

export function isNotNullOrUndefined<T>(object: T | undefined | null): object is T {
  return !!object
}

export function last<T>(array?: T[]): T | undefined {
  return array?.[array.length - 1]
}

export function sortBy<T, S extends string | number>(array: T[], getValue: (element: T) => S): T[] {
  const values = array.map(getValue)
  const valuesAreNumbers = values.every((value) => typeof value === 'number')

  // eslint-disable-next-line fp/no-mutating-methods
  const sorted = !valuesAreNumbers ? values.sort() : values.sort((a, b) => (a as number) - (b as number))

  return sorted.reduce(
    ({ indicesUsed, sortedArray }: { indicesUsed: number[]; sortedArray: T[] }, value) => {
      const nextIndex = array.findIndex((element, index) => !indicesUsed.includes(index) && getValue(element) === value)
      if (nextIndex < 0) return { indicesUsed, sortedArray }
      return {
        indicesUsed: [...indicesUsed, nextIndex],
        sortedArray: [...sortedArray, array[nextIndex]],
      }
    },
    { indicesUsed: [], sortedArray: [] },
  ).sortedArray
}

export function lastBy<T, S>(array: T[], getValue: (element: T) => S): T | undefined {
  const sortedArray = sortBy(array, getValue)
  return last(sortedArray)
}

export function firstBy<T, S>(array: T[], getValue: (element: T) => S): T | undefined {
  const sortedArray = sortBy(array, getValue)
  return sortedArray[0]
}

export function validateEmail(email = ''): boolean {
  /**
   * a) This pattern uses a negative lookahead assertion (?!.*(?:__|\.\.|[\._]{2}))
   *    to ensure that there are no two consecutive dots or underscores in the string.
   * b) [a-zA-Z0-9] allows alphanumeric characters only no special characters.
   * c) [a-zA-Z0-9._+-]? allows alphanumeric characters, dots, underscores, plus signs and hyphens.
   * d) [a-zA-Z0-9]+ ensures that the string ends with alphanumeric characters only.
   * e) @[a-zA-Z0-9.-]+ ensures that the string contains @ followed by alphanumeric characters, dots and hyphens.
   * f) [a-zA-Z]{2,4} ensures that the TLD part has a minimum length of 2 and maximum length of 4.
   * g) The $ anchor ensures that the string ends with the TLD part.
   */
  /* eslint-disable-next-line no-useless-escape */
  const reg = /^(?!.*(?:__|\.\.|[\._]{2}))[a-zA-Z0-9]+([a-zA-Z0-9._-]?)+[a-zA-Z0-9]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/
  return reg.test(email)
}

export function validateBirthYear(birthYear: number): boolean {
  return birthYear >= 1870 && birthYear <= new Date().getFullYear()
}

export function isTouchDevice(): boolean {
  return typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0)
}

export function partitionBy<T>(arr: T[], separator: (element: T) => boolean): [T[], T[]] {
  return arr.reduce(
    ([accMain, accAlt]: [T[], T[]], element) => {
      const separateChest = separator(element)
      return separateChest ? [accMain, [...accAlt, element]] : [[...accMain, element], accAlt]
    },
    [[], []],
  )
}

const INTL_LOCALE_CODE: Record<AppLanguage, string> = {
  [AppLanguage.EN]: 'en-GB',
  [AppLanguage.NO]: 'nb-NO',
  [AppLanguage.UA]: 'uk',
}

export function formatDate(date: Date, language: string): string {
  const locale = INTL_LOCALE_CODE[language as AppLanguage]

  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZone: 'Europe/Oslo',
    hour12: false,
  }
  return capitalizeAll(date.toLocaleString(locale, options))
    .split('')
    .filter((char) => char !== ',')
    .join('')
}

export function capitalize(str: string): string {
  return str
    .split('')
    .map((char, index) => (index === 0 ? char.toUpperCase() : char))
    .join('')
}

function capitalizeAll(str: string): string {
  return str.split(' ').map(capitalize).join(' ')
}

export function uniqBy<T, S>(arr: T[], getValue: (element: T) => S): T[] {
  const valuesUsed: T[] = []
  arr.forEach((element) => {
    const elementValue = getValue(element)
    if (valuesUsed.some((value) => getValue(value) === elementValue)) {
      return []
    }
    valuesUsed.push(element) // eslint-disable-line
  })

  return valuesUsed
}

export function cleanNullAndUndefined<T>(object: T): Partial<{ [K in keyof T]: NonNullable<T[K]> }> {
  return Object.entries(object).reduce((acc, [key, value]) => {
    if (value === undefined || value === null) {
      return acc
    }

    return {
      ...acc,
      [key]: value,
    }
  }, {})
}

export function clean<T>(object: T): Partial<{ [K in keyof T]: NonNullable<T[K]> }> {
  if (!object) return object
  return Object.entries(object).reduce((acc, [key, value]) => {
    if (!value) return acc
    return {
      ...acc,
      [key]: value,
    }
  }, {})
}

interface ClassNames {
  [key: string]: string | ClassNames
}

function extractClassNames(classNames: Array<string | ClassNames>): Array<string | ClassNames> {
  if (classNames.some((elem) => typeof elem === 'object')) {
    const strings = classNames.filter((elem) => typeof elem === 'string')
    const objects = classNames.filter((elem) => typeof elem === 'object')
    return [...strings, ...objects.flatMap((elem) => extractClassNames(Object.values(elem)))]
  } else {
    return classNames
  }
}

export function applyClassNames(classNames: ClassNames): string {
  return extractClassNames(Object.values(classNames)).join(' ')
}

export const getPanelType = (role: UserRole | undefined): UserRole => {
  switch (role) {
    case UserRole.USER:
    case UserRole.ADMIN:
    case UserRole.TUTOR:
      return role

    default:
      return UserRole.USER
  }
}

export function constructPhoneNumberWithCallingCode(callingCode: string, phoneNumber: string): string {
  return `${callingCode}:${phoneNumber}`
}

export function isValidPhoneNumberInput(phoneNumber: string): boolean {
  // Check if number only contains digits, no spaces allowed.
  return /^[0-9]*$/.test(phoneNumber)
}

export function parsePhoneNumberWithCallingCode(phoneNumber: string | null | undefined): string {
  return phoneNumber ? phoneNumber.replace(':', ' ') : ''
}

/**
 * Check the phone number against a regular expression. Matches "COUNTRY_CALLING_CODE:PHONE_NUMBER".
 * Examples: "+47:12345678", "+1:1234567890", "+880:1234567890"
 * @param phoneNumber - A phone number with a caller code from either user input or a database.
 * @returns {boolean}
 */
export function isValidPhoneNumber(phoneNumber: string): boolean {
  return /^(\+{1}[0-9]+):([0-9]+)$/.test(phoneNumber)
}

/**
 * Extract the country calling code and the country alpha-2 code from the phone number.
 * @param phoneNumber - A phone number with a caller code from either user input or a database.
 * @returns {[countryCallingCode, countryAlpha2Code]}
 */
export function getCountryCodesFromPhoneNumber(phoneNumber: string | null | undefined): [string, string] {
  if (phoneNumber && isValidPhoneNumber(phoneNumber)) {
    const [callingCode] = phoneNumber.split(':')

    const phoneNumberCountry = countryData.find((country) => {
      return country.callingCode === callingCode
    })

    if (phoneNumberCountry) {
      return [phoneNumberCountry.callingCode, phoneNumberCountry.code]
    }
  }

  return ['', '']
}

/**
 * Get the country calling code from an alpha-2 country code.
 * @param countryCode - An alpha-2 country code. Examples: 'NO', 'US', 'BR'.
 * @returns {string}
 */
export function getCountryCallingCodeFromAlpha2Code(countryCode: string | undefined): string | undefined {
  return countryData.find(({ code }) => code === countryCode)?.callingCode
}

export function getBrowserTimezone(): string | undefined {
  return typeof Intl !== undefined ? Intl.DateTimeFormat().resolvedOptions().timeZone : ''
}

export function formatThousands(num: number | string): string {
  const integer = typeof num === 'number' ? num : parseFloat(num)
  const commaSeparatedString = integer.toLocaleString('en-US')

  return commaSeparatedString.replace(/,/g, ' ')
}

export function getUserSkillLabel(skill: UserSkill | null | undefined, t: TFunction): string {
  switch (skill) {
    case UserSkill.WEAK:
      return t('generics:skill.weak')

    case UserSkill.A_BIT_WEAK:
      return t('generics:skill.aBitWeak')

    case UserSkill.AVERAGE:
      return t('generics:skill.average')

    case UserSkill.STRONG:
      return t('generics:skill.strong')

    case UserSkill.VERY_STRONG:
      return t('generics:skill.veryStrong')

    default:
      return ''
  }
}

export function transformPreference(preference?: Preference<'from-api-response'>): Preference | undefined {
  if (typeof preference !== 'undefined') {
    return {
      ...preference,
      timeslots: constructTimeslotFields(preference?.timeslots),
    }
  } else {
    return preference
  }
}

export function constructTimeslotFields(timeslots: Timeslot[] | undefined): PreferredTime[] {
  return (
    timeslots?.map(({ weekday, startTime, endTime }) => ({
      day: weekday,
      startHour: startTime / (60 * 60),
      endHour: endTime / (60 * 60),
    })) || []
  )
}

export function rangeBetween(start: number, end: number): number[] {
  return new Array(end - start).fill(0).map((d, i) => i + start)
}

export function cleanNullAndUndefined2<T>(obj: T): Partial<T> {
  return cleanDeep(obj, { emptyArrays: false })
}

export function shuffle<T>(array: T[]): T[] {
  const arrCopy = [...array]

  range(arrCopy.length).forEach((index) => {
    const randomIndex = Math.round(Math.random() * index)
    ;[arrCopy[index], arrCopy[randomIndex]] = [arrCopy[randomIndex], arrCopy[index]]
  })

  return arrCopy
}

export function isStagingEnvironment(): boolean {
  return process.env.NEXT_PUBLIC_ENVIRONMENT === 'staging'
}

export function batchArray<T>(arr: T[], size: number): T[][] {
  return arr.reduce(
    (acc: T[][], it) => {
      if (acc[acc.length - 1].length < size) {
        return [...acc.slice(0, acc.length - 1), [...acc[acc.length - 1], it]]
      } else {
        return [...acc, [it]]
      }
    },
    [[]],
  )
}

/**
 * Takes an object lireral as input and trimes out the empty spaces from
 * object properties and returns it, supports nested object
 * @param obj object literal
 * @returns {object}
 */

export function trimObjectProperties(obj: Record<string, any>, exclude?: string[]): Record<string, any> {
  if (!obj || typeof obj !== 'object') throw new Error(`${typeof obj}is not an object`)
  const excludeKeys = exclude ?? []

  const entries = Object.entries(obj)

  const trimmed: Array<[string, any]> = entries.map(([key, value]) => {
    let trimmedValue = value

    if (typeof value === 'string') {
      trimmedValue = excludeKeys.includes(key) ? value : value.trim()
    } else if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      trimmedValue = trimObjectProperties(value)
    }

    return [key, trimmedValue]
  })

  return fromEntries(trimmed)
}

export function fromEntries<S>(arr: Array<[string | number, S]>): Record<string | number, S> {
  return arr.reduce((acc, [key, value]) => {
    return {
      ...acc,
      [key]: value,
    }
  }, {})
}

export function isLastSubject(topic: string, missingTopics: string[]): boolean {
  return missingTopics.indexOf(topic) + 1 === missingTopics.length
}

export function getVideoTypeFromUrl(url?: string): string | undefined {
  if (!url) return undefined
  const regex = /\.([a-zA-Z0-9]+)$/ // match the file extension at the end of the URL
  const match = regex.exec(url)
  if (match) {
    const extension = match[1].toLowerCase() // get the file extension and convert to lowercase
    switch (extension) {
      case 'mp4':
        return 'video/mp4'
      case 'webm':
        return 'video/webm'
      case 'ogg':
        return 'video/ogg'
      case 'm3u8':
        return 'application/x-mpegURL'
      case 'mpd':
        return 'application/dash+xml'
      // add additional cases for other supported video types as needed
      default:
        return undefined // unrecognized file type
    }
  } else {
    return undefined // no file extension found in the URL
  }
}

const getLanguageLabel = (srclang: AppLanguage): string => {
  switch (srclang) {
    case AppLanguage.EN:
      return 'English'
    case AppLanguage.NO:
      return 'Norwegian'
    case AppLanguage.UA:
      return 'Ukrainian'
    default:
      return 'English'
  }
}

export function getFormattedVideoUrls(videoUrls: string[]): SourceType[] {
  if (!videoUrls) return []
  return videoUrls
    .map((videoUrl) => {
      const type = getVideoTypeFromUrl(videoUrl)
      return type ? { src: videoUrl, type } : undefined
    })
    .filter((source): source is SourceType => source !== undefined)
}

export function getFormattedVideoTracks(videoTracks: any[]): TrackType[] {
  if (!videoTracks) return []
  return videoTracks.map((videoTrack) => {
    return {
      src: videoTrack.src,
      kind: videoTrack.kind,
      label: getLanguageLabel(videoTrack.srclang),
      srclang: videoTrack.srclang,
    }
  })
}

export function calculateGemCount(gemType: string, gemCount: number): number {
  switch (gemType) {
    case 'red':
      return gemCount * 1
    case 'green':
      return gemCount * 10
    case 'blue':
      return gemCount * 100
    default:
      return gemCount
  }
}

export function getTotalGemCount(gemRed = 0, gemGreen = 0, gemBlue = 0): number {
  return calculateGemCount('red', gemRed) + calculateGemCount('green', gemGreen) + calculateGemCount('blue', gemBlue)
}

export function addSuffixToNumber(num: number): string {
  const suffixes = ['th', 'st', 'nd', 'rd']
  const value = num % 100
  const suffix = value >= 11 && value <= 13 ? 'th' : suffixes[value % 10] || 'th'
  return `${num}${suffix}`
}

export function getLongestWordLength(sentence: string): number {
  return sentence
    .split(/\s+/)
    .map((word) => word.replace(/[^\w]/g, ''))
    .reduce((max, word) => Math.max(max, word.length), 0)
}

export function arrayContainsAll<T>(array1: T[], array2: T[]): boolean {
  return array2.every((val) => array1.includes(val))
}

export interface TitleValuePair<T extends string> {
  value: T
  title: string
}

export function enumToList<T extends Record<string, string>>(enumObj: T): Array<TitleValuePair<string>> {
  return Object.entries(enumObj).map(([title, value]) => ({
    title,
    value,
  }))
}

export const delayInMs = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

export function isEmptyOrNullString(value: string | null | undefined): boolean {
  return !(typeof value === 'string' && value.trim() !== '')
}

export const getRandomInteger = (max: number): number => Math.floor(Math.random() * max)
